import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import client from '../../../services/graphqlClient';
import './ActividadesCapitulo.css';
import ModalCrearActividad from './ModalCrearActividad';
import ModalEvidencias from './ModalEvidencias';

const ActividadesCapitulo = ({ proyectoId, capitulo, proveedores, onVolver, onProyectoActualizado }) => {
  const { success, error } = useNotification();
  const [actividades, setActividades] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [actividadEditando, setActividadEditando] = useState(null);
  const [mostrarEvidencias, setMostrarEvidencias] = useState(false);
  const [actividadEvidencias, setActividadEvidencias] = useState(null);

  useEffect(() => {
    cargarActividades();
  }, [proyectoId, capitulo.id]);

  const cargarActividades = async () => {
    try {
      setLoading(true);
      const data = await client.query(`
        query($capitulo_id: Int!) {
          actividades(capitulo_id: $capitulo_id) {
            actividades {
              id proyecto_id capitulo_id proveedor_id descripcion valor orden
              fecha_realizacion costo valor_ejecutado evidencia_url fecha_ejecucion
              cantidad unidad factura_id notas estado responsable costo_presupuestado
              proveedor_nombre proveedor_tipo capitulo_nombre numero_factura
              proyecto_nombre total_evidencias total_fotos total_videos
            }
            estadisticas { total_actividades costo_total total_ejecutado completadas en_proceso con_proveedor }
          }
        }
      `, { capitulo_id: capitulo.id });

      setActividades(data.actividades.actividades || []);
      setEstadisticas(data.actividades.estadisticas);
    } catch (error) {
      console.error('Error al cargar actividades:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActividadCreada = () => {
    cargarActividades();
  };

  // Cambiar estado directamente desde la tarjeta
  const handleCambiarEstado = async (actividadId, nuevoEstado) => {
    try {
      const data = await client.mutate(`
        mutation($actividad_id: Int!, $estado: String!) {
          cambiarEstadoActividad(actividad_id: $actividad_id, estado: $estado) { success message }
        }
      `, { actividad_id: actividadId, estado: nuevoEstado });

      if (data.cambiarEstadoActividad.success) {
        success('Estado actualizado');
        cargarActividades();
      } else {
        error('Error: ' + data.cambiarEstadoActividad.message);
      }
    } catch (err) {
      error('Error al cambiar estado');
    }
  };

  const formatMoney = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);
  };

  const formatDate = (fecha) => {
    if (!fecha) return 'Sin fecha';
    const date = new Date(fecha);
    if (isNaN(date.getTime())) return 'Sin fecha';
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getEstadoOptions = () => [
    { value: 'planificado', label: '📅 Planificado', class: 'planificado' },
    { value: 'en_proceso', label: '🔄 En Proceso', class: 'en-proceso' },
    { value: 'completado', label: '✅ Completado', class: 'completado' },
    { value: 'cancelado', label: '❌ Cancelado', class: 'cancelado' }
  ];

  const getEstadoBadge = (estado) => {
    const estados = {
      completado: { label: '✅ Completado', class: 'completado' },
      en_proceso: { label: '🔄 En Proceso', class: 'en-proceso' },
      planificado: { label: '📅 Planificado', class: 'planificado' },
      cancelado: { label: '❌ Cancelado', class: 'cancelado' }
    };
    return estados[estado] || estados.completado;
  };

  const getProveedorIcon = (tipo) => {
    const iconos = {
      materiales: '🏗️',
      mano_obra: '👷',
      servicios: '🔧',
      transporte: '🚚',
      equipos: '⚙️'
    };
    return iconos[tipo] || '📦';
  };

  if (loading) {
    return (
      <div className="actividades-loading">
        <div className="loading-spinner">⏳</div>
        <p>Cargando actividades...</p>
      </div>
    );
  }

  return (
    <div className="actividades-capitulo">
      {/* Header */}
      <div className="actividades-header">
        <div className="header-info">
          <h2>📋 Actividades - Capítulo {capitulo.numero}</h2>
          <p className="capitulo-nombre">{capitulo.nombre}</p>
        </div>
        <div className="header-actions">
          {onVolver && (
            <button className="btn-volver" onClick={onVolver}>
              ⬅️ Volver
            </button>
          )}
          {/* Botón Nueva Actividad oculto */}
        </div>
      </div>

      {/* Estadísticas */}
      {estadisticas && (
        <div className="actividades-stats">
          <div className="stat-card">
            <span className="stat-icon">📊</span>
            <div className="stat-content">
              <span className="stat-value">{estadisticas.total_actividades}</span>
              <span className="stat-label">Total Actividades</span>
            </div>
          </div>

          <div className="stat-card">
            <span className="stat-icon">💰</span>
            <div className="stat-content">
              <span className="stat-value">{formatMoney(estadisticas.costo_total)}</span>
              <span className="stat-label">Costo Total</span>
            </div>
          </div>

          <div className="stat-card">
            <span className="stat-icon">✅</span>
            <div className="stat-content">
              <span className="stat-value">{estadisticas.completadas}</span>
              <span className="stat-label">Completadas</span>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Actividades */}
      {actividades.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📝</span>
          <h3>No hay actividades registradas</h3>
          <p>Comienza agregando la primera actividad para este capítulo</p>
          <button className="btn-primera-actividad" onClick={() => setMostrarModal(true)}>
            ➕ Agregar Primera Actividad
          </button>
        </div>
      ) : (
        <div className="actividades-lista">
          {actividades.map((actividad) => {
            const estadoBadge = getEstadoBadge(actividad.estado);
            const costoTotal = actividad.costo_presupuestado || actividad.valor || (actividad.costo * actividad.cantidad);
            const valorEjecutado = actividad.valor_ejecutado || 0;
            const saldoDisponible = costoTotal - valorEjecutado;
            const porcentajeEjecutado = costoTotal > 0 ? Math.round((valorEjecutado / costoTotal) * 100) : 0;

            return (
              <div key={actividad.id} className={`actividad-card ${estadoBadge.class}`}>
                <div className="actividad-header">
                  <div className="actividad-fecha">
                    <span className="fecha-icon">📅</span>
                    <span>{formatDate(actividad.fecha_realizacion)}</span>
                  </div>

                  {/* Selector de estado directamente en la tarjeta */}
                  <select
                    className={`estado-select ${actividad.estado}`}
                    value={actividad.estado}
                    onChange={(e) => handleCambiarEstado(actividad.id, e.target.value)}
                  >
                    {getEstadoOptions().map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="actividad-body">
                  <p className="actividad-descripcion">{actividad.descripcion}</p>

                  {actividad.valor > 0 && (
                    <div style={{ fontSize: '0.82rem', color: '#059669', fontWeight: 700, margin: '4px 0' }}>
                      $ {Number(actividad.valor).toLocaleString('es-CO')}
                    </div>
                  )}

                  {actividad.proveedor_nombre && (
                    <div className="actividad-proveedor">
                      <span>{getProveedorIcon(actividad.proveedor_tipo)}</span>
                      <span>{actividad.proveedor_nombre}</span>
                    </div>
                  )}
                </div>

                <div className="actividad-footer">
                  <button
                    className="btn-evidencias"
                    onClick={() => {
                      setActividadEvidencias(actividad);
                      setMostrarEvidencias(true);
                    }}
                    title="Ver/Agregar evidencias"
                  >
                    📸 Evidencia General ({actividad.total_evidencias || 0})
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modales */}
      <ModalCrearActividad
        show={mostrarModal}
        onClose={() => {
          setMostrarModal(false);
          setActividadEditando(null);
        }}
        proyectoId={proyectoId}
        capitulo={capitulo}
        proveedores={proveedores}
        actividadEditando={actividadEditando}
        onActividadGuardada={handleActividadCreada}
      />

      <ModalEvidencias
        show={mostrarEvidencias}
        onClose={() => {
          setMostrarEvidencias(false);
          setActividadEvidencias(null);
          cargarActividades();
        }}
        actividad={actividadEvidencias}
      />
    </div>
  );
};

export default ActividadesCapitulo;
